import { useState } from 'react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines, faHourglassHalf, faSpinner, faCircleCheck,
  faCircleXmark, faArrowRight, faUserSecret, faCommentDots,
  faChartBar, faTag
} from '@fortawesome/free-solid-svg-icons'
import AdminSidebar from '../../components/admin/Sidebar'
import AdminTopbar from '../../components/admin/Topbar'

const STATUS_CONFIG = {
  menunggu: { label: 'Menunggu',  bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   dot: 'bg-amber-400'   },
  diproses: { label: 'Diproses',  bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200',     dot: 'bg-sky-400'     },
  selesai:  { label: 'Selesai',   bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-400' },
  ditolak:  { label: 'Ditolak',   bg: 'bg-red-50',     text: 'text-red-700',     ring: 'ring-red-200',     dot: 'bg-red-400'     },
}

export default function AdminDashboard({ current_user, stats, recent_reports, category_counts, monthly_trend }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const total = stats?.total ?? 0

  const statCards = [
    {
      label:      'Total Laporan',
      value:      total,
      sub:        `${stats?.this_month ?? 0} laporan bulan ini`,
      icon:       faFileLines,
      iconBg:     'bg-blue-100',
      iconColor:  'text-blue-600',
      borderColor: 'border-l-blue-500',
    },
    {
      label:      'Menunggu',
      value:      stats?.menunggu ?? 0,
      sub:        'Perlu ditindaklanjuti',
      icon:       faHourglassHalf,
      iconBg:     'bg-amber-100',
      iconColor:  'text-amber-600',
      borderColor: 'border-l-amber-500',
      urgent:     (stats?.menunggu ?? 0) > 0,
    },
    {
      label:      'Sedang Diproses',
      value:      stats?.diproses ?? 0,
      sub:        'Dalam penanganan',
      icon:       faSpinner,
      iconBg:     'bg-sky-100',
      iconColor:  'text-sky-600',
      borderColor: 'border-l-sky-500',
    },
    {
      label:      'Selesai',
      value:      stats?.selesai ?? 0,
      sub:        total > 0 ? `${Math.round(((stats?.selesai ?? 0) / total) * 100)}% dari total` : '—',
      icon:       faCircleCheck,
      iconBg:     'bg-emerald-100',
      iconColor:  'text-emerald-600',
      borderColor: 'border-l-emerald-500',
    },
    {
      label:      'Ditolak',
      value:      stats?.ditolak ?? 0,
      sub:        total > 0 ? `${Math.round(((stats?.ditolak ?? 0) / total) * 100)}% dari total` : '—',
      icon:       faCircleXmark,
      iconBg:     'bg-red-100',
      iconColor:  'text-red-600',
      borderColor: 'border-l-red-500',
    },
  ]

  const maxMonthly = Math.max(...(monthly_trend ?? []).map(m => m.count), 1)

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <AdminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminTopbar
          current_user={current_user}
          onMenuToggle={() => setSidebarOpen(true)}
          placeholder="Cari laporan, pengguna..."
        />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Selamat datang, <span className="font-medium text-gray-700">{current_user?.name}</span>
              {' — '}
              {new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Urgent banner — shown when there are pending reports */}
          {(stats?.menunggu ?? 0) > 0 && (
            <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="w-7 h-7 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faHourglassHalf} className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <p className="text-sm text-amber-800">
                Ada <span className="font-semibold">{stats.menunggu} laporan</span> yang menunggu tindak lanjut.
              </p>
              <a href="/admin/reports?status=menunggu" className="ml-auto text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap flex items-center gap-1">
                Tinjau sekarang <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
            {statCards.map(card => (
              <div
                key={card.label}
                className={`bg-white rounded-xl border border-gray-200 border-l-4 ${card.borderColor} p-4 flex items-start gap-3${card.urgent ? ' ring-1 ring-amber-200' : ''}`}
              >
                <div className={`w-9 h-9 ${card.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${card.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-gray-900 leading-none">{card.value}</div>
                  <div className="text-[11px] font-medium text-gray-500 mt-0.5 truncate">{card.label}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5 truncate">{card.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent reports table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">Laporan Terbaru</h2>
                <p className="text-xs text-gray-400 mt-0.5">8 laporan terakhir yang masuk ke sistem</p>
              </div>
              <a href="/admin/reports" className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                Lihat semua <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
              </a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-[11px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <th className="text-left px-5 py-3">Kode</th>
                    <th className="text-left px-5 py-3">Judul Laporan</th>
                    <th className="text-left px-5 py-3">Kategori</th>
                    <th className="text-left px-5 py-3">Pelapor</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Tanggal</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(recent_reports ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-400">
                        Belum ada laporan masuk
                      </td>
                    </tr>
                  ) : (recent_reports ?? []).map(row => {
                    const sc = STATUS_CONFIG[row.status] ?? STATUS_CONFIG.menunggu
                    return (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3.5 font-mono text-xs text-gray-400 whitespace-nowrap">{row.tracking_code}</td>
                        <td className="px-5 py-3.5 text-gray-800 font-medium max-w-[200px] truncate">{row.title}</td>
                        <td className="px-5 py-3.5">
                          <span className="text-xs text-gray-600 bg-gray-100 rounded-md px-2 py-1 whitespace-nowrap">{row.category}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          {row.anonymous ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                              <FontAwesomeIcon icon={faUserSecret} className="w-3 h-3" />
                              Anonim
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600 truncate max-w-[120px] block">{row.reporter_name}</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${sc.bg} ${sc.text} ${sc.ring}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{row.created_at}</td>
                        <td className="px-5 py-3.5">
                          <a href={`/admin/reports/${row.id}`} className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                            Detail →
                          </a>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Category distribution */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">Distribusi Kategori</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Sebaran laporan berdasarkan jenis pelanggaran</p>
                </div>
                <FontAwesomeIcon icon={faTag} className="w-4 h-4 text-gray-300" />
              </div>
              {(category_counts ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Belum ada data kategori</p>
              ) : (
                <div className="space-y-3">
                  {(category_counts ?? []).map(({ category, count }) => {
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0
                    return (
                      <div key={category}>
                        <div className="flex justify-between items-center text-xs mb-1.5">
                          <span className="text-gray-600 truncate max-w-[220px]">{category}</span>
                          <span className="font-semibold text-gray-800 ml-2 whitespace-nowrap">
                            {count} <span className="text-gray-400 font-normal">({pct}%)</span>
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-400 rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, 1)}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-4">

              {/* Monthly mini bar chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-800">Tren 6 Bulan</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Laporan masuk per bulan</p>
                  </div>
                  <FontAwesomeIcon icon={faChartBar} className="w-4 h-4 text-gray-300" />
                </div>
                <div className="flex items-end gap-1.5 h-16 mt-2">
                  {(monthly_trend ?? []).map(({ label, count }) => {
                    const h = maxMonthly > 0 ? Math.max(Math.round((count / maxMonthly) * 52), 4) : 4
                    return (
                      <div key={label} className="flex-1 flex flex-col items-center gap-1 group" title={`${label}: ${count} laporan`}>
                        <span className="text-[9px] text-gray-500 invisible group-hover:visible">{count}</span>
                        <div className="w-full bg-blue-400 rounded-t-sm" style={{ height: `${h}px` }} />
                        <span className="text-[9px] text-gray-400">{label.split(' ')[0]}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Quick stats */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Info Tambahan</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FontAwesomeIcon icon={faUserSecret} className="w-3.5 h-3.5 text-gray-400" />
                      Laporan Anonim
                    </div>
                    <span className="text-sm font-bold text-gray-800">{stats?.anonymous ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FontAwesomeIcon icon={faCommentDots} className="w-3.5 h-3.5 text-gray-400" />
                      Sudah Ditanggapi
                    </div>
                    <span className="text-sm font-bold text-gray-800">{stats?.has_feedback ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FontAwesomeIcon icon={faFileLines} className="w-3.5 h-3.5 text-gray-400" />
                      Bulan Ini
                    </div>
                    <span className="text-sm font-bold text-gray-800">{stats?.this_month ?? 0}</span>
                  </div>
                  {total > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-gray-500">Tingkat Penyelesaian</span>
                        <span className="font-semibold text-emerald-600">
                          {Math.round(((stats?.selesai ?? 0) / total) * 100)}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                          style={{ width: `${Math.round(((stats?.selesai ?? 0) / total) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

        </main>
      </div>
    </div>
  )
}
